import {useMemo} from 'react';
import {useSyncExternalStore} from 'use-sync-external-store/shim';

import type {ContentPart} from '../store/thread';
import {
  type SendMessageOptions,
  type WBAgentController,
  type WBAgentControllerStore,
  wbAgentControllerStore,
} from './store';

/**
 * useWBAgentController exposes the agent chat, whether it's available and
 * various interactions, to any component in the app. `store` defaults to the
 * shared app-wide instance; tests pass their own for isolation.
 */
export function useWBAgentController(
  store: WBAgentControllerStore = wbAgentControllerStore
): WBAgentController & {isAvailable: boolean} {
  // NOTE: isAvailable trails the live runtime by one commit. Fine for buttons,
  // but never gate a useAui-rendering component on it; those belong inside
  // AgentChatRuntimeBoundary.
  const {isAvailable} = useSyncExternalStore(
    store.subscribe,
    store.getAvailability
  );

  const actions = useMemo(
    () => ({
      openChat: () => store.getHandle()?.openChat(),
      sendMessage: (
        prompt: string,
        attachments?: ContentPart[],
        opts?: SendMessageOptions
      ) => store.getHandle()?.sendMessage(prompt, attachments, opts),
    }),
    [store]
  );

  return {isAvailable, ...actions};
}
